// Fills and prunes the offline image cache. The caller decides ordering
// (thumbnails before full-size — see the design spec); this module decides
// mechanics: skip-if-cached, bounded concurrency, quota handling.
import { IMAGE_CACHE } from "./db";

/** Modest on purpose: this runs silently in the background on page loads. */
const CONCURRENCY = 4;

function isQuotaError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "QuotaExceededError";
}

export async function syncImages(
  urls: string[]
): Promise<{ fetched: number; failed: number; quotaExceeded: boolean }> {
  const cache = await caches.open(IMAGE_CACHE);
  const cachedPaths = new Set((await cache.keys()).map((r) => new URL(r.url).pathname));
  const missing = urls.filter((u) => !cachedPaths.has(u));

  let fetched = 0;
  let failed = 0;
  let quotaExceeded = false;
  let cursor = 0;

  async function worker() {
    while (cursor < missing.length && !quotaExceeded) {
      const url = missing[cursor++];
      try {
        // cache: "no-store" is load-bearing — see the design spec: Chrome's
        // HTTP cache holds no-cors <img> responses and hands them to
        // cors-mode fetches, which then fail the CORS check.
        const res = await fetch(url, { mode: "cors", credentials: "same-origin", cache: "no-store" });
        if (!res.ok) {
          failed += 1;
          continue;
        }
        // Re-wrap: never store a redirected response as-is.
        const body = await res.blob();
        await cache.put(
          url,
          new Response(body, {
            headers: { "content-type": res.headers.get("content-type") ?? "image/webp" },
          })
        );
        fetched += 1;
      } catch (e) {
        if (isQuotaError(e)) {
          quotaExceeded = true;
          return;
        }
        failed += 1;
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, missing.length) }, worker));
  return { fetched, failed, quotaExceeded };
}

export async function pruneImages(valid: Set<string>): Promise<number> {
  const cache = await caches.open(IMAGE_CACHE);
  const keys = await cache.keys();
  let deleted = 0;
  for (const req of keys) {
    const path = new URL(req.url).pathname;
    if (path.startsWith("/uploads/") && !valid.has(path)) {
      if (await cache.delete(req)) deleted += 1;
    }
  }
  return deleted;
}
